import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  HiArrowsPointingIn,
  HiArrowsPointingOut,
  HiMicrophone,
  HiOutlineComputerDesktop,
  HiPhoneXMark,
  HiSpeakerWave,
  HiXCircle,
} from 'react-icons/hi2';
import { useVoiceCall } from '../../VoiceCallContext';
import { getAvatarColor, getFirstTwoLetters } from '../../common';
import { techColors, neonGlow } from '../../styles/TechRetroEffects';
import { useT } from '../../LocaleContext';

const Shell = styled.div`
  position: fixed;
  z-index: ${({ $theater }) => ($theater ? 560 : 510)};
  pointer-events: none;
  ${({ $expanded, $theater }) => ($theater ? css`
    inset: 0;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    padding: 0;
  ` : $expanded ? css`
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: max(72px, calc(env(safe-area-inset-top) + 56px)) 16px max(16px, env(safe-area-inset-bottom));
  ` : css`
    top: max(16px, calc(env(safe-area-inset-top) + 8px));
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    max-width: calc(100vw - 24px);
  `)}

  @media (max-width: 768px) {
    ${({ $expanded }) => (!$expanded ? css`
      top: auto;
      bottom: max(16px, env(safe-area-inset-bottom));
      left: 12px;
      right: 12px;
      transform: none;
      max-width: none;
      width: auto;
    ` : '')}
  }
`;

const Backdrop = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
  cursor: pointer;
  pointer-events: auto;
`;

const Panel = styled.div`
  pointer-events: auto;
  position: relative;
  background: ${techColors.darkBgSecondary};
  border: 1px solid ${techColors.borderSubtle};
  ${neonGlow(techColors.neonCyan, 'subtle')}
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  ${({ $expanded, $wide, $theater }) => ($theater ? css`
    width: 100%;
    height: 100%;
    max-width: none;
    padding: max(10px, env(safe-area-inset-top)) 16px max(12px, env(safe-area-inset-bottom));
    border-radius: 0;
    border: none;
    display: flex;
    flex-direction: column;
    text-align: center;
    margin-top: 0;
    background: #07080a;
  ` : $expanded ? css`
    width: min(${$wide ? '720px' : '420px'}, calc(100vw - 32px));
    padding: 24px 22px 20px;
    border-radius: 14px;
    text-align: center;
    margin-top: 0;
  ` : css`
    min-width: min(340px, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
    padding: 10px 12px 10px 14px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
  `)}

  @media (max-width: 768px) {
    ${({ $expanded, $theater }) => ($theater ? '' : !$expanded ? css`
      min-width: 0;
      max-width: none;
      width: 100%;
      border-radius: 16px;
      padding: 12px 14px;
    ` : css`
      width: calc(100vw - 24px);
      max-width: none;
      border-radius: 16px;
    `)}
  }
`;

const MiniAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => $color || techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  font-size: 0.85em;
  font-weight: 600;
  border: 2px solid ${techColors.borderSubtle};
`;

const MiniAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MiniInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const MiniName = styled.span`
  color: ${techColors.textPrimary};
  font-size: 0.95em;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MiniMeta = styled.span`
  color: ${techColors.neonCyan};
  font-size: 0.75em;
  font-variant-numeric: tabular-nums;
`;

const MiniActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  position: relative;
  width: ${({ $size }) => ($size === 'lg' ? '52px' : '40px')};
  height: ${({ $size }) => ($size === 'lg' ? '52px' : '40px')};
  border-radius: 50%;
  border: 1px solid ${({ $active, $muted, $danger, $level }) => {
    if ($danger) return 'transparent';
    if ($active) return techColors.neonCyan;
    if ($muted) return techColors.borderSubtle;
    if (typeof $level === 'number') return `rgba(76, 175, 80, ${0.35 + $level * 0.65})`;
    return techColors.borderSubtle;
  }};
  background: ${({ $danger, $active }) => {
    if ($danger) return techColors.danger;
    if ($active) return 'rgba(0, 229, 255, 0.16)';
    return techColors.darkBgTertiary;
  }};
  color: ${techColors.textPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => ($size === 'lg' ? '1.3em' : '1.1em')};
  cursor: pointer;
  overflow: hidden;
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  flex-shrink: 0;
  transition: opacity 0.15s ease, border-color 0.1s ease;

  &:hover {
    opacity: 1;
  }
`;

const MicFill = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${({ $level }) => `${Math.round($level * 100)}%`};
  background: linear-gradient(
    to top,
    rgba(76, 175, 80, 0.95) 0%,
    rgba(129, 199, 132, 0.75) 100%
  );
  transition: height 0.07s ease-out;
  pointer-events: none;
`;

const MicIcon = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;

  ${({ $muted }) => $muted && `
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 130%;
      height: 2px;
      background: ${techColors.danger};
      transform: translate(-50%, -50%) rotate(-40deg);
      border-radius: 2px;
      box-shadow: 0 0 0 1px ${techColors.darkBgTertiary};
    }
  `}
`;

const ExpandedHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  flex-shrink: 0;
`;

const Label = styled.p`
  margin: 0;
  color: ${techColors.textSecondary};
  font-size: 0.72em;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  text-align: left;
`;

const Duration = styled.p`
  margin: 6px 0 0;
  color: ${techColors.neonCyan};
  font-size: 1.45em;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  text-align: left;
`;

const MinimizeButton = styled.button`
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${techColors.borderSubtle};
  background: ${techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.78em;
  letter-spacing: 0.04em;
  text-transform: lowercase;

  &:hover {
    border-color: ${techColors.borderGlow};
  }
`;

const Participants = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  gap: 28px;
  margin: 20px 0 24px;
`;

const ScreenFrame = styled.div`
  position: relative;
  width: 100%;
  margin: 8px 0 4px;
  border-radius: 12px;
  overflow: hidden;
  background: #0a0a0a;
  border: 1px solid ${techColors.borderSubtle};
  ${({ $theater }) => ($theater ? css`
    flex: 1;
    min-height: 0;
    margin: 8px 0 12px;
    aspect-ratio: auto;
    max-height: none;
    border-radius: 10px;
  ` : css`
    aspect-ratio: 16 / 10;
    max-height: min(42vh, 360px);
    cursor: zoom-in;
  `)}
`;

const ScreenExpandButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${techColors.borderSubtle};
  background: rgba(0, 0, 0, 0.62);
  color: ${techColors.textPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.15em;

  &:hover {
    border-color: ${techColors.neonCyan};
  }
`;

const ScreenVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

const ScreenCaption = styled.span`
  position: absolute;
  left: 10px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.55);
  color: ${techColors.textPrimary};
  font-size: 0.72em;
  letter-spacing: 0.04em;
  text-transform: lowercase;
`;

const ScreenPreview = ({ stream, label, theater, onToggleTheater }) => {
  const t = useT();
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return undefined;
    }
    const nextTrack = stream?.getVideoTracks?.()?.[0] || null;
    const currentTrack = el.srcObject?.getVideoTracks?.()?.[0] || null;
    if (currentTrack?.id !== nextTrack?.id) {
      el.srcObject = nextTrack ? new MediaStream([nextTrack]) : null;
      if (nextTrack) {
        el.play().catch(() => {});
      }
    }
    return undefined;
  }, [stream]);

  if (!stream) {
    return null;
  }

  const toggleLabel = theater ? t('call.exitFullscreen') : t('call.expand');

  return (
    <ScreenFrame
      $theater={theater}
      onClick={theater ? undefined : onToggleTheater}
      role={theater ? undefined : 'button'}
      title={theater ? undefined : toggleLabel}
    >
      <ScreenVideo ref={videoRef} autoPlay playsInline muted />
      {label ? <ScreenCaption>{label}</ScreenCaption> : null}
      <ScreenExpandButton
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onToggleTheater();
        }}
        aria-label={toggleLabel}
        title={toggleLabel}
      >
        {theater ? <HiArrowsPointingIn /> : <HiArrowsPointingOut />}
      </ScreenExpandButton>
    </ScreenFrame>
  );
};

const Participant = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
  max-width: 150px;
`;

const AvatarRing = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  padding: 3px;
  background: ${({ $active }) => (
    $active
      ? `linear-gradient(135deg, ${techColors.neonCyan}, ${techColors.neonGreen})`
      : techColors.borderSubtle
  )};
`;

const Avatar = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => $color || techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  font-size: 1.5em;
  font-weight: 600;
  border: 2px solid ${techColors.darkBgSecondary};
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Name = styled.span`
  color: ${techColors.textPrimary};
  font-size: 0.95em;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const Role = styled.span`
  color: ${techColors.textSecondary};
  font-size: 0.75em;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-shrink: 0;
`;

const NoiseOption = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  color: ${techColors.textSecondary};
  font-size: 0.82em;
  cursor: pointer;
  user-select: none;

  input {
    accent-color: ${techColors.neonCyan};
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
`;

const ShareAudioHint = styled.p`
  margin: 12px 0 0;
  color: ${techColors.terminalYellow};
  font-size: 0.78em;
  line-height: 1.4;
  text-align: center;
`;

const MixerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  border: 1px solid ${techColors.borderSubtle};
`;

const MixerRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${techColors.textSecondary};
  font-size: 0.82em;
  cursor: pointer;
  user-select: none;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  span {
    min-width: 52px;
    white-space: nowrap;
  }

  input[type='range'] {
    flex: 1;
    height: 4px;
    accent-color: ${techColors.neonCyan};
    cursor: pointer;
  }
`;

const MixerValue = styled.span`
  min-width: 32px !important;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const CallAvatar = ({ name, seed, imageUrl, active, role }) => (
  <Participant>
    <AvatarRing $active={active}>
      <Avatar $color={imageUrl ? undefined : getAvatarColor(seed || name || '')}>
        {imageUrl ? (
          <AvatarImage src={imageUrl} alt={name || 'user'} />
        ) : (
          getFirstTwoLetters(name || '?')
        )}
      </Avatar>
    </AvatarRing>
    <Name>{name || 'unknown'}</Name>
    <Role>{role}</Role>
  </Participant>
);

const ActiveCallScreen = () => {
  const t = useT();
  const {
    isIncoming,
    activeCall,
    callProfiles,
    remoteAudioReady,
    callState,
    isMuted,
    micLevel,
    noiseSuppression,
    setNoiseSuppression,
    toggleMute,
    canShareScreen,
    isSharingScreen,
    screenShareHasAudio,
    localScreenStream,
    remoteScreenStream,
    startScreenShare,
    stopScreenShare,
    endCall,
    terminateGroupRoom,
    isGroupCall,
    isGroupHost,
    groupParticipants,
    peerVolume,
    changePeerVolume,
    screenVolume,
    changeScreenVolume,
  } = useVoiceCall();

  const [elapsed, setElapsed] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [screenTheater, setScreenTheater] = useState(false);
  const isOutgoing = callState === 'outgoing' && !isGroupCall;
  const canMinimize = !isOutgoing;
  const isLive = isOutgoing
    || callState === 'connecting'
    || callState === 'active'
    || remoteAudioReady;
  const isConnected = remoteAudioReady
    || callState === 'active'
    || (isGroupCall && callState === 'connecting' && (groupParticipants?.length || 0) <= 1);
  const showExpanded = expanded || isOutgoing;
  const screenStream = remoteScreenStream || localScreenStream;
  const screenLabel = remoteScreenStream
    ? t('call.screen')
    : (isSharingScreen ? t('call.yourScreen') : '');
  const isScreenLive = Boolean(screenStream);
  const isTheater = screenTheater && isScreenLive;

  useEffect(() => {
    if (!isScreenLive) {
      setScreenTheater(false);
    }
  }, [isScreenLive]);

  useEffect(() => {
    if (isScreenLive) {
      setExpanded(true);
    }
  }, [isScreenLive]);

  useEffect(() => {
    if (!isLive || !activeCall?.call_id) {
      setElapsed(0);
      setExpanded(true);
      return undefined;
    }
    // Always open the call panel expanded (DM + group); user can minimize manually.
    setExpanded(true);
    return undefined;
  }, [isLive, activeCall?.call_id]);

  useEffect(() => {
    if (!isConnected || !activeCall?.call_id) {
      setElapsed(0);
      return undefined;
    }
    const startedAt = Date.now();
    setElapsed(0);
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isConnected, activeCall?.call_id]);

  useEffect(() => {
    if (!showExpanded || !canMinimize) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (isTheater) {
        setScreenTheater(false);
        return;
      }
      setExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showExpanded, canMinimize, isTheater]);

  if (!activeCall || isIncoming || !isLive) {
    return null;
  }

  const peerName = isGroupCall
    ? (callProfiles.peerName || activeCall.peer_name || t('chat.group'))
    : (callProfiles.peerName || activeCall.peer_name || t('call.voiceCall'));
  const selfName = callProfiles.selfName || 'you';
  const statusLabel = isConnected
    ? t('call.connected')
    : (isOutgoing ? t('call.calling') : t('call.connecting'));
  const displayMicLevel = isMuted ? 0 : micLevel;
  const durationLabel = isConnected ? formatDuration(elapsed) : '--:--';
  const leaveLabel = isGroupCall ? t('call.leave') : t('call.endCall');
  const muteLabel = isMuted ? t('call.unmute') : t('call.mute');
  const shareLabel = isSharingScreen ? t('call.stopSharing') : t('call.shareScreen');
  const minimizeLabel = isTheater ? t('call.exitFullscreen') : t('call.minimize');
  const expandLabel = t('call.expand');
  const endEveryoneLabel = t('call.endForEveryone');

  const stopBubble = (event) => {
    event.stopPropagation();
  };

  const onToggleMute = (event) => {
    event.stopPropagation();
    event.preventDefault();
    toggleMute();
  };

  const onToggleScreenShare = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (isSharingScreen) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  };

  const minimize = () => {
    if (isTheater) {
      setScreenTheater(false);
      return;
    }
    if (canMinimize) {
      setExpanded(false);
    }
  };

  const toggleScreenTheater = (event) => {
    event?.stopPropagation?.();
    if (!isScreenLive) {
      return;
    }
    setExpanded(true);
    setScreenTheater((open) => !open);
  };

  return (
    <Shell $expanded={showExpanded} $theater={isTheater}>
      {showExpanded && canMinimize && !isTheater && (
        <Backdrop
          type="button"
          aria-label={t('call.minimize')}
          onClick={minimize}
        />
      )}

      {showExpanded ? (
        <Panel $expanded $wide={isScreenLive} $theater={isTheater}>
          <ExpandedHeader>
            <div>
              <Label>{isGroupCall ? peerName : t('call.voiceCall')} · {statusLabel}{isScreenLive ? ` · ${t('call.screen')}` : ''}</Label>
              <Duration>{durationLabel}</Duration>
            </div>
            {canMinimize && (
              <MinimizeButton
                type="button"
                onClick={minimize}
                aria-label={minimizeLabel}
                title={minimizeLabel}
              >
                <HiArrowsPointingIn />
                {minimizeLabel}
              </MinimizeButton>
            )}
          </ExpandedHeader>

          <ScreenPreview
            stream={screenStream}
            label={screenLabel}
            theater={isTheater}
            onToggleTheater={toggleScreenTheater}
          />

          {!isTheater && (
          <Participants>
            {isGroupCall ? (
              (groupParticipants.length
                ? groupParticipants
                : [{ user_id: callProfiles.selfId, user_name: selfName }]
              ).map((participant) => {
                const isSelf = participant.user_id === callProfiles.selfId;
                return (
                  <CallAvatar
                    key={participant.user_id || participant.user_name}
                    name={participant.user_name || 'user'}
                    seed={participant.user_id || participant.user_name}
                    imageUrl={isSelf ? callProfiles.selfAvatarUrl : null}
                    active={isConnected}
                    role={isSelf ? 'you' : 'peer'}
                  />
                );
              })
            ) : (
              <>
                <CallAvatar
                  name={peerName}
                  seed={activeCall.peer_id || peerName}
                  imageUrl={callProfiles.peerAvatarUrl}
                  active={isConnected}
                  role="peer"
                />
                <CallAvatar
                  name={selfName}
                  seed={callProfiles.selfId || selfName}
                  imageUrl={callProfiles.selfAvatarUrl}
                  active
                  role="you"
                />
              </>
            )}
          </Participants>
          )}

          <Controls>
            <IconButton
              type="button"
              $size="lg"
              onClick={onToggleMute}
              $muted={isMuted}
              $level={displayMicLevel}
              aria-label={muteLabel}
              title={muteLabel}
            >
              <MicFill $level={displayMicLevel} />
              <MicIcon $muted={isMuted}>
                <HiMicrophone />
              </MicIcon>
            </IconButton>
            {canShareScreen && (
              <IconButton
                type="button"
                $size="lg"
                $active={isSharingScreen}
                onClick={onToggleScreenShare}
                aria-label={shareLabel}
                title={shareLabel}
              >
                <HiOutlineComputerDesktop />
              </IconButton>
            )}
            {canMinimize && (
              <IconButton
                type="button"
                $size="lg"
                onClick={minimize}
                aria-label={minimizeLabel}
                title={minimizeLabel}
              >
                <HiArrowsPointingIn />
              </IconButton>
            )}
            <IconButton
              type="button"
              $size="lg"
              $danger
              onClick={endCall}
              aria-label={leaveLabel}
              title={leaveLabel}
            >
              <HiPhoneXMark />
            </IconButton>
            {isGroupCall && isGroupHost && (
              <IconButton
                type="button"
                $size="lg"
                $danger
                onClick={terminateGroupRoom}
                aria-label={endEveryoneLabel}
                title={endEveryoneLabel}
              >
                <HiXCircle />
              </IconButton>
            )}
          </Controls>

          {!isTheater && (
          <MixerSection>
            <MixerRow>
              <HiSpeakerWave />
              <span>{t('call.peers')}</span>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(peerVolume * 100)}
                onChange={(e) => changePeerVolume(Number(e.target.value) / 100)}
              />
              <MixerValue>{Math.round(peerVolume * 100)}%</MixerValue>
            </MixerRow>
            {isSharingScreen && screenShareHasAudio && (
              <MixerRow>
                <HiOutlineComputerDesktop />
                <span>{t('call.screen')}</span>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={Math.round(screenVolume * 100)}
                  onChange={(e) => changeScreenVolume(Number(e.target.value) / 100)}
                />
                <MixerValue>{Math.round(screenVolume * 100)}%</MixerValue>
              </MixerRow>
            )}
          </MixerSection>
          )}

          {!isTheater && (
          <NoiseOption>
            <input
              type="checkbox"
              checked={noiseSuppression}
              onChange={(event) => {
                setNoiseSuppression(event.target.checked);
              }}
            />
            {t('call.noiseSuppression')}
          </NoiseOption>
          )}
          {!isTheater && isSharingScreen && !screenShareHasAudio && (
            <ShareAudioHint>
              no tab audio — in the chrome dialog turn on
              «также предоставить доступ к аудио на вкладке», then share
            </ShareAudioHint>
          )}
        </Panel>
      ) : (
        <Panel
          $expanded={false}
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setExpanded(true);
            }
          }}
          aria-label={expandLabel}
          title={expandLabel}
        >
          <MiniAvatar
            $color={isGroupCall || callProfiles.peerAvatarUrl
              ? undefined
              : getAvatarColor(activeCall.peer_id || peerName)}
          >
            {!isGroupCall && callProfiles.peerAvatarUrl ? (
              <MiniAvatarImage src={callProfiles.peerAvatarUrl} alt={peerName} />
            ) : (
              getFirstTwoLetters(peerName)
            )}
          </MiniAvatar>

          <MiniInfo>
            <MiniName>{peerName}</MiniName>
            <MiniMeta>
              {statusLabel}
              {isGroupCall ? ` · ${(groupParticipants || []).length}` : ''}
              {isScreenLive ? ` · ${t('call.screen')}` : ''}
              {isConnected ? ` · ${durationLabel}` : ''}
            </MiniMeta>
          </MiniInfo>

          <MiniActions onClick={stopBubble} onKeyDown={stopBubble}>
            <IconButton
              type="button"
              onClick={onToggleMute}
              $muted={isMuted}
              $level={displayMicLevel}
              aria-label={muteLabel}
              title={muteLabel}
            >
              <MicFill $level={displayMicLevel} />
              <MicIcon $muted={isMuted}>
                <HiMicrophone />
              </MicIcon>
            </IconButton>
            {canShareScreen && (
              <IconButton
                type="button"
                $active={isSharingScreen}
                onClick={onToggleScreenShare}
                aria-label={shareLabel}
                title={shareLabel}
              >
                <HiOutlineComputerDesktop />
              </IconButton>
            )}
            <IconButton
              type="button"
              onClick={() => setExpanded(true)}
              aria-label={expandLabel}
              title={expandLabel}
            >
              <HiArrowsPointingOut />
            </IconButton>
            <IconButton
              type="button"
              $danger
              onClick={endCall}
              aria-label={leaveLabel}
              title={leaveLabel}
            >
              <HiPhoneXMark />
            </IconButton>
            {isGroupCall && isGroupHost && (
              <IconButton
                type="button"
                $danger
                onClick={terminateGroupRoom}
                aria-label={endEveryoneLabel}
                title={endEveryoneLabel}
              >
                <HiXCircle />
              </IconButton>
            )}
          </MiniActions>
        </Panel>
      )}
    </Shell>
  );
};

export default ActiveCallScreen;
